"use client";

import { motion } from "motion/react";
import { AnimatedBackground } from "@/components/auth/animated-background";
import { springPresets } from "@/lib/motion";

interface AuthLayoutProps {
  children: React.ReactNode;
}

const featureCards = [
  {
    title: "Done-for-you pipeline",
    description: "From CSV to qualified leads, automated",
  },
  {
    title: "Pre-warmed email delivery",
    description: "High deliverability from day one",
  },
  {
    title: "AI-powered reply sorting",
    description: "Interested sellers surface instantly",
  },
];

/**
 * Split-screen auth layout.
 * - Desktop (lg+): Left decorative panel (55%) + right form panel (45%)
 * - Mobile: Full-screen form with subtle background
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen">
      {/* --- Decorative panel (hidden on mobile) --- */}
      <div className="relative hidden lg:flex lg:w-[55%] items-center justify-center overflow-hidden">
        {/* Gradient base */}
        <div
          className="absolute inset-0"
          style={{ background: "var(--mesh-bg)" }}
        />

        {/* Animated orbs */}
        <AnimatedBackground />

        {/* Brand content */}
        <motion.div
          className="relative z-10 px-12 text-center"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={springPresets.default}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo.webp" alt="Abound" width={200} height={40} className="h-10 w-auto mb-4" />
          <p className="text-heading-3 text-muted-foreground max-w-md mx-auto">
            Seller outreach, simplified. Turn raw property data into a qualified
            seller pipeline in under 24 hours.
          </p>

          {/* Glass feature cards */}
          <motion.div
            className="mt-10 flex flex-col gap-3 max-w-sm mx-auto"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
            }}
          >
            {featureCards.map((card) => (
              <motion.div
                key={card.title}
                className="glass-1 rounded-xl px-5 py-3 text-left"
                variants={{
                  initial: { opacity: 0, y: 12 },
                  animate: { opacity: 1, y: 0 },
                }}
                transition={springPresets.gentle}
              >
                <p className="text-body-medium text-foreground">{card.title}</p>
                <p className="text-caption text-muted-foreground">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* --- Form panel --- */}
      <div className="relative flex w-full lg:w-[45%] items-center justify-center px-6 py-12">
        {/* Subtle background orbs on mobile only */}
        <div className="absolute inset-0 lg:hidden overflow-hidden">
          <AnimatedBackground />
        </div>

        <motion.div
          className="relative z-10 w-full max-w-md"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={springPresets.default}
        >
          {/* Mobile-only logo */}
          <div className="mb-8 text-center lg:hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo.webp" alt="Abound" width={160} height={32} className="h-8 w-auto" />
            <p className="mt-1 text-caption text-muted-foreground">
              Seller outreach, simplified.
            </p>
          </div>

          {children}
        </motion.div>
      </div>
    </div>
  );
}
